import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useCreateMandatoryCourse } from '../../queries/moodle/useCreateMandatoryCourse';

const schema = z
  .object({
    year: z.string().min(1, 'Year is required'),
    moodleCourseId: z.number({ required_error: 'Course is required' }),
    courseNameSnapshot: z.string().min(1, 'Course name is required'),
    isMandatory: z.boolean().default(true),
    graceDays: z.coerce.number().min(0, 'Must be 0 or more'),
    freezeDays: z.coerce.number().int().min(0, 'Must be 0 or more'),
    launchDate: z.string().min(1, 'Launch date is required'),
    endDate: z.string().min(1, 'End date is required'),
    isActive: z.boolean().default(true),
    recurrence: z.enum(['YEARLY', 'MONTHLY', 'ONCE']),
    warningNotificationDays: z.array(z.number()).default([]),
    completionCcEmails: z
      .array(z.string().email('Invalid email address'))
      .default([]),
    applicableLocationIds: z.array(z.number()).default([]),
  })
  .superRefine((data, ctx) => {
    if (data.warningNotificationDays.some((val) => val > data.graceDays)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Values must be <= ${data.graceDays}`,
        path: ['warningNotificationDays'],
      });
    }
  });

export const useCreateCourseForm = (onSuccess) => {
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      year: String(new Date().getFullYear()),
      isMandatory: true,
      isActive: true,
      recurrence: 'YEARLY',
      graceDays: 0,
      freezeDays: 0,
      warningNotificationDays: [],
      completionCcEmails: [],
      applicableLocationIds: [],
    },
  });

  const { mutate, isPending } = useCreateMandatoryCourse(onSuccess);

  const onSubmit = form.handleSubmit(({ graceDays, ...rest }) =>
    mutate({
      ...rest,
      applicability: 'ALL',
      graceDays,
    }),
  );

  return { form, onSubmit, isPending };
};
